import { useState, useEffect, useCallback } from "react";
import {
  Calendar,
  Eye,
  Loader2,
  X,
  Check,
  ChevronDown,
  ChevronRight,
  Mail,
  UserX,
  Users,
} from "lucide-react";
import { get, post, del } from "../api/client";
import { ScheduledRun } from "../types";

// ── Toast ──────────────────────────────────────────────────────────────────────

interface ToastProps {
  message: string;
  type: "success" | "error";
  onDismiss: () => void;
}

function Toast({ message, type, onDismiss }: ToastProps) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all duration-300 max-w-sm w-full mx-4 ${
        type === "success" ? "bg-green-600 text-white" : "bg-red-600 text-white"
      }`}
    >
      {type === "success" ? <Check size={16} /> : <X size={16} />}
      <span className="flex-1">{message}</span>
      <button
        onClick={onDismiss}
        className="p-1 rounded hover:bg-white/20 transition-colors"
        aria-label="Sluiten"
      >
        <X size={14} />
      </button>
    </div>
  );
}

// ── Loading / Error / Empty ────────────────────────────────────────────────────

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-waves-blue border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6 text-sm">
      <strong>Fout bij laden:</strong> {message}
    </div>
  );
}

// ── Status badge ───────────────────────────────────────────────────────────────

function RunStatusBadge({ status }: { status: ScheduledRun["status"] }) {
  const map: Record<ScheduledRun["status"], { label: string; cls: string }> = {
    pending: { label: "In wachtrij", cls: "bg-slate-100 text-slate-600" },
    kam_warned: { label: "KAM gewaarschuwd", cls: "bg-blue-100 text-blue-700" },
    completed: { label: "Verzonden", cls: "bg-green-100 text-green-700" },
    cancelled: { label: "Geannuleerd", cls: "bg-red-100 text-red-600" },
  };
  const { label, cls } = map[status] ?? map.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

// ── HMP row ────────────────────────────────────────────────────────────────────

interface HMPRowProps {
  hmp: ScheduledRun["hmps"][number];
  runId: number;
  onExclude: (runId: number, hmpId: number) => void;
  onInclude: (runId: number, hmpId: number) => void;
  actionLoading: boolean;
}

function HMPRow({ hmp, runId, onExclude, onInclude, actionLoading }: HMPRowProps) {
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 ${
        hmp.excluded ? "bg-slate-50 opacity-60" : "hover:bg-slate-50/50"
      } transition-colors`}
    >
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
        style={{ backgroundColor: hmp.excluded ? "#94a3b8" : "#7C3AED" }}
      >
        {hmp.name.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-800 truncate">{hmp.name}</div>
        <div className="text-xs text-slate-400 truncate">{hmp.email}</div>
        {hmp.kam_name && (
          <div className="text-xs text-slate-400">KAM: {hmp.kam_name}</div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Preview */}
        <button
          onClick={() =>
            window.open(`/api/admin/mail/scheduled-runs/${runId}/preview/${hmp.id}`, "_blank")
          }
          className="p-1.5 rounded hover:bg-waves-blue-light text-slate-400 hover:text-waves-blue transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
          title="Voorbeeld van te versturen mail"
        >
          <Eye size={14} />
        </button>

        {/* Exclude / include toggle */}
        {hmp.excluded ? (
          <button
            onClick={() => onInclude(runId, hmp.id)}
            disabled={actionLoading}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 transition-colors min-h-[36px] disabled:opacity-50"
            title="Uitsluiting ongedaan maken"
          >
            {actionLoading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Check size={12} />
            )}
            Herstellen
          </button>
        ) : (
          <button
            onClick={() => onExclude(runId, hmp.id)}
            disabled={actionLoading}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors min-h-[36px] disabled:opacity-50"
            title="Uitsluiten van deze ronde"
          >
            {actionLoading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <UserX size={12} />
            )}
            Uitsluiten
          </button>
        )}
      </div>
    </div>
  );
}

// ── Run card ───────────────────────────────────────────────────────────────────

interface RunCardProps {
  run: ScheduledRun;
  onExclude: (runId: number, hmpIds: number[]) => Promise<void>;
  onInclude: (runId: number, hmpId: number) => Promise<void>;
  onRunUpdate: (updated: ScheduledRun) => void;
}

function RunCard({ run, onExclude, onInclude, onRunUpdate }: RunCardProps) {
  const [expanded, setExpanded] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [rowLoading, setRowLoading] = useState<number | null>(null);

  const activeHmps = run.hmps.filter((h) => !h.excluded);
  const excludedHmps = run.hmps.filter((h) => h.excluded);

  async function handleExcludeAll() {
    if (activeHmps.length === 0) return;
    setBulkLoading(true);
    try {
      await onExclude(run.id, activeHmps.map((h) => h.id));
    } finally {
      setBulkLoading(false);
    }
  }

  async function handleIncludeAll() {
    if (excludedHmps.length === 0) return;
    setBulkLoading(true);
    try {
      for (const h of excludedHmps) {
        await onInclude(run.id, h.id);
      }
    } finally {
      setBulkLoading(false);
    }
  }

  async function handleExcludeOne(runId: number, hmpId: number) {
    setRowLoading(hmpId);
    try {
      await onExclude(runId, [hmpId]);
    } finally {
      setRowLoading(null);
    }
  }

  async function handleIncludeOne(runId: number, hmpId: number) {
    setRowLoading(hmpId);
    try {
      await onInclude(runId, hmpId);
    } finally {
      setRowLoading(null);
    }
  }

  const scheduledDate = new Date(run.scheduled_for);
  const dateLabel = scheduledDate.toLocaleDateString("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const timeLabel = scheduledDate.toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className="card overflow-hidden">
      {/* Card header */}
      <div
        className="flex items-center gap-3 px-4 md:px-5 py-4 cursor-pointer select-none hover:bg-slate-50 transition-colors min-h-[56px]"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="text-slate-400 flex-shrink-0">
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </span>

        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: "#E0EFF8" }}
        >
          <Mail size={16} style={{ color: "#65ADDD" }} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 truncate">{run.partner_name}</span>
            <RunStatusBadge status={run.status} />
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {dateLabel} om {timeLabel}
            <span className="ml-2 text-slate-400">
              {run.hmp_count} HMP{run.hmp_count !== 1 ? "s" : ""}
              {excludedHmps.length > 0 && (
                <span className="text-amber-600 ml-1">
                  ({excludedHmps.length} uitgesloten)
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-slate-100">
          {/* Bulk actions */}
          {run.hmps.length > 0 && (
            <div className="px-4 md:px-5 py-3 flex items-center gap-3 bg-slate-50/50 border-b border-slate-100">
              <span className="text-xs font-medium text-slate-500 flex-1">
                {activeHmps.length} actief · {excludedHmps.length} uitgesloten
              </span>
              <button
                onClick={handleExcludeAll}
                disabled={bulkLoading || activeHmps.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-40 min-h-[36px]"
              >
                {bulkLoading ? <Loader2 size={12} className="animate-spin" /> : <UserX size={12} />}
                Alles uitsluiten
              </button>
              <button
                onClick={handleIncludeAll}
                disabled={bulkLoading || excludedHmps.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 transition-colors disabled:opacity-40 min-h-[36px]"
              >
                {bulkLoading ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                Alle uitsluitingen ongedaan
              </button>
            </div>
          )}

          {/* HMP list */}
          {run.hmps.length === 0 ? (
            <div className="py-10 text-center text-slate-400 text-sm">
              Geen HMPs gevonden voor deze ronde.
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {run.hmps.map((hmp) => (
                <HMPRow
                  key={hmp.id}
                  hmp={hmp}
                  runId={run.id}
                  onExclude={handleExcludeOne}
                  onInclude={handleIncludeOne}
                  actionLoading={rowLoading === hmp.id}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function AankomendeMails() {
  const [runs, setRuns] = useState<ScheduledRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const fetchRuns = useCallback(() => {
    setLoading(true);
    get<{ runs: ScheduledRun[] }>("/admin/mail/scheduled-runs/upcoming")
      .then((data) => setRuns(data.runs))
      .catch((err) => setError(err instanceof Error ? err.message : "Laden mislukt."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  async function handleExclude(runId: number, hmpIds: number[]) {
    try {
      await post(`/admin/mail/scheduled-runs/${runId}/exclude`, { hmp_ids: hmpIds });
      setRuns((prev) =>
        prev.map((r) =>
          r.id === runId
            ? {
                ...r,
                hmps: r.hmps.map((h) =>
                  hmpIds.includes(h.id) ? { ...h, excluded: true } : h
                ),
              }
            : r
        )
      );
      setToast({
        message: `${hmpIds.length} HMP${hmpIds.length !== 1 ? "s" : ""} uitgesloten van ronde`,
        type: "success",
      });
    } catch (err: unknown) {
      setToast({
        message: err instanceof Error ? err.message : "Uitsluiten mislukt.",
        type: "error",
      });
    }
  }

  async function handleInclude(runId: number, hmpId: number) {
    try {
      await del(`/admin/mail/scheduled-runs/${runId}/exclude/${hmpId}`);
      setRuns((prev) =>
        prev.map((r) =>
          r.id === runId
            ? {
                ...r,
                hmps: r.hmps.map((h) =>
                  h.id === hmpId ? { ...h, excluded: false } : h
                ),
              }
            : r
        )
      );
      setToast({ message: "Uitsluiting ongedaan gemaakt", type: "success" });
    } catch (err: unknown) {
      setToast({
        message: err instanceof Error ? err.message : "Herstellen mislukt.",
        type: "error",
      });
    }
  }

  function handleRunUpdate(updated: ScheduledRun) {
    setRuns((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  }

  // Group runs by partner
  const partnerGroups = runs.reduce<Record<string, ScheduledRun[]>>((acc, run) => {
    const key = run.partner_name;
    if (!acc[key]) acc[key] = [];
    acc[key].push(run);
    return acc;
  }, {});

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Calendar size={20} style={{ color: "#65ADDD" }} />
            Aankomende mails
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Overzicht van geplande review-mailronden, gegroepeerd per partner.
          </p>
        </div>
        <button
          onClick={fetchRuns}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors min-h-[44px] sm:min-h-0"
        >
          Vernieuwen
        </button>
      </div>

      {/* Content */}
      {runs.length === 0 ? (
        <div className="card p-12 text-center text-slate-400">
          <Users size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Geen aankomende mailronden gevonden.</p>
          <p className="text-xs text-slate-300 mt-1">
            Controleer of review-mails zijn ingeschakeld voor partners in Tab Configuratie.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(partnerGroups).map(([partnerName, partnerRuns]) => (
            <div key={partnerName}>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">
                {partnerName}
              </h3>
              <div className="space-y-3">
                {partnerRuns.map((run) => (
                  <RunCard
                    key={run.id}
                    run={run}
                    onExclude={handleExclude}
                    onInclude={handleInclude}
                    onRunUpdate={handleRunUpdate}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}
